import { i18n } from 'next-i18next';

import { translationFallbackApiInstance } from '@kc-console/context';

import { SUPPORTED_LANGUAGES } from '../constants';
import { isTranslation } from '../types';

export interface FallbackResult {
  failureCount: number;
  successCount: number;
}

/**
 * 여러 네임스페이스의 번역을 fallback에서 병렬로 가져옵니다.
 *
 * @param namespaces - 업데이트가 필요한 네임스페이스 목록
 * @returns 성공/실패 카운트
 */
export async function getTranslationsFromFallback(
  namespaces: string[],
  languages: string[] = SUPPORTED_LANGUAGES,
): Promise<FallbackResult> {
  const { fetchFallbackTranslation } = translationFallbackApiInstance();

  const promises = namespaces.flatMap(namespace =>
    languages.map(async language => {
      try {
        const translation = await fetchFallbackTranslation(language, namespace);

        if (!isTranslation(translation)) {
          console.error(
            `[i18n FALLBACK ERROR] Invalid translation data format for "${namespace}" (${language})`,
          );
          return {
            language,
            namespace,
            success: false,
          };
        }

        i18n?.addResourceBundle(language, namespace, translation, true, true);

        return {
          language,
          namespace,
          success: true,
        };
      } catch {
        return {
          language,
          namespace,
          success: false,
        };
      }
    }),
  );

  const results = await Promise.allSettled(promises);

  let successCount = 0;
  let failureCount = 0;

  for (const result of results) {
    if (result.status === 'fulfilled' && result.value.success) {
      successCount++;
    } else {
      failureCount++;
    }
  }

  return {
    successCount,
    failureCount,
  };
}
